import { isFunction, isString } from "lodash";
import { BADGE_ID_MAPPER } from '@/datas/auto-expire-badge';
import { VENDORS_PRODUCTS_KEY, getVendorsProducts, clearVendorsProductsCache } from '@/datas/vendors-products';
import {
  PLAN_BEHAVIOR_ANALYZE_KEY,
  getPlanBehaviorAnalyze,
  clearPlanBehaviorAnalyzeCache,
} from '@/datas/plan-behavior-analyze';

function showResolvedDialog(resolvedComponent, props, events = {}, options = {}) {
  if (resolvedComponent && resolvedComponent.default && window.Vue) {
    const tpl = window.Vue.extend(resolvedComponent.default);
    const vm = new tpl({
      propsData: Object.assign(props, { visible: true }),
    });

    // Handle class attribute
    if (options.class) {
      if (typeof options.class === 'string') {
        vm.$el?.classList.add(options.class);
      } else if (typeof options.class === 'object') {
        Object.keys(options.class).forEach(className => {
          if (options.class[className]) {
            vm.$el?.classList.add(className);
          }
        });
      }
    }

    // Handle scopedSlots
    if (options.scopedSlots) {
      Object.keys(options.scopedSlots).forEach(slotName => {
        const slotContent = options.scopedSlots[slotName];
        if (typeof slotContent === 'function') {
          // Create a new scopedSlots object with existing slots plus new ones
          vm.$scopedSlots = Object.assign({}, vm.$scopedSlots, {
            [slotName]: slotContent
          });
        }
      });
    }

    Object.keys(events).forEach(eventName => {
      const eventHandler = events[eventName];
      if (isFunction(eventHandler)) {
        vm.$on(eventName, eventHandler);
      }
    });
    const el = vm.$mount().$el;

    // Apply class after mounting
    if (options.class) {
      if (typeof options.class === 'string') {
        el.classList.add(options.class);
      } else if (typeof options.class === 'object') {
        Object.keys(options.class).forEach(className => {
          if (options.class[className]) {
            el.classList.add(className);
          }
        });
      }
    }

    // event
    vm.$on('closed', () => {
      document.body.removeChild(el);
      vm.$destroy();
      if (isFunction(events?.onClose)) {
        events.onClose()
      }
    });

    document.body.appendChild(el);
    if (options?.showIns) {
      return vm;
    }
    return true;
  } else {
    return false;
  }
}
/**
 *
 * @param {Object} component: The component exported in @/components/index.js, or dynamically import
 * @param {Object} props: The props pass to component
 * @param {Object} events: Event handlers for the component
 * @param {Object} options: Additional options including class and scopedSlots
 * @description Provide a function for caller to show a dialog easily.
 * Please Note:
 * 1. Emit 'closed' event after dialog closed
 * 2. options.class: CSS class string or object to apply to the dialog
 * 3. options.scopedSlots: Object containing scoped slot functions
 */
export function showDialog(component, props = { visible: true }, events = {}, options = {}) {
  return new Promise((resolve) => {
    if (typeof component === 'function' && window.Vue) {
      component().then((resolvedComponent) => {
        resolve(showResolvedDialog(resolvedComponent, props, events, options))
      });
    } else if (typeof component === 'object' && component.default) {
      resolve(showResolvedDialog(component, props, events, options))
    } else {
      resolve(false);
    }
  })
}

function doMount(resolvedComponent, rootId, props, events) {
  console.assert(rootId && isString(rootId) && document.getElementById(rootId), 'invalid root id', rootId)
  if (rootId && isString(rootId) && document.getElementById(rootId)) {
    const tpl = window.Vue.extend(resolvedComponent.default);
    const vm = new tpl({
      propsData: Object.assign(props),
    });
    Object.keys(events).forEach(eventName => {
      const eventHandler = events[eventName];
      if (isFunction(eventHandler)) {
        vm.$on(eventName, eventHandler);
      }
    });
    vm.$mount("#" + rootId)
    return vm;
  }
}
export function mountVueComponet(component, rootId, props = {}, events = {}) {
  return new Promise((resolve) => {
    if (typeof component === 'function' && window.Vue) {
      component().then((resolvedComponent) => {
        resolve(doMount(resolvedComponent, rootId, props, events))
      });
    } else if (typeof component === 'object' && component.default) {
      resolve(doMount(component, rootId, props, events))
    } else {
      resolve(false);
    }
  })
}

window.__ZM_BILLING_SHARED = Object.assign(window.__ZM_BILLING_SHARED || {}, {
  apis: {
    getVendorsProducts,
    clearVendorsProductsCache,
    getPlanBehaviorAnalyze,
    clearPlanBehaviorAnalyzeCache,
  },
  utils: {
    showDialog,
    mountVueComponet,
  },
  constants: {
    VENDORS_PRODUCTS_KEY,
    PLAN_BEHAVIOR_ANALYZE_KEY,
    AUTO_EXPIRE_BADGE: { BADGE_ID_MAPPER }
  }
});
